import { useQuery } from '@tanstack/react-query';
import { getTokenFromStorage } from '@/utils/auth';
import { getCampaign } from '@/api/beatlyApi';
import type { ClientCampaign } from '@/api/beatlyApi';

export interface UseCampaignParams {
  campaignId: string | undefined;
  enabled?: boolean;
}

/** API expects campaign IDs like "123456-cpg"; avoid calling with invalid ids (e.g. "3") to prevent 404/500. */
function isValidCampaignId(id: string | undefined): boolean {
  return Boolean(id && id.length >= 8 && id.includes('-'));
}

/**
 * Fetches a single campaign by id (full detail).
 * Same API as beatly-web-app: GET /app/v2/client/campaign/:campaignId
 */
export function useCampaign({ campaignId, enabled = true }: UseCampaignParams) {
  const token = getTokenFromStorage();
  const shouldFetch = enabled && Boolean(token && campaignId) && isValidCampaignId(campaignId);

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['client', 'campaign', campaignId],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID is required');
      return getCampaign(campaignId, token ?? '', 'en');
    },
    enabled: shouldFetch,
  });

  return {
    data: data as ClientCampaign | undefined,
    isLoading,
    error,
    isError,
  };
}
